import React from 'react';
import { Link } from 'react-router-dom';
import './TermsAndConditions.css';

function TermsAndConditionsPage() {
  return (
    <div className='TermsAndConditionsPage'>
        <div className="terms-conditions-document">
    <header className="doc-header">
        <h1>Terms and Conditions</h1>
        <p className="effective-date">Effective Date: January 15, 2025</p>
    </header>

    <section className="doc-section">
        <p>Welcome to RoomEase! These Terms and Conditions (“Terms”) govern your access to and use of our mobile application (“App”) and services. By downloading, installing, or using RoomEase, you agree to be bound by these Terms. If you do not agree, please refrain from using the App.</p>
    </section>

    <section className="doc-section">
        <h2>1. Eligibility</h2>
        <ul>
            <li>You must be at least 18 years old to use RoomEase.</li>
            <li>You agree to provide accurate and complete information during registration.</li>
            <li><strong>False or Incomplete Information:</strong> Providing false or incomplete registration information may result in account suspension or termination.</li>
        </ul>
        <p>By using RoomEase, you consent to the collection, storage, and use of your personal data as described herein.</p>
    </section>

    <section className="doc-section">
        <h2>2. User Accounts</h2>
        <ul>
            <li><strong>Account Responsibility:</strong> You are responsible for maintaining the confidentiality of your login credentials.</li>
            <li><strong>Account Sharing:</strong> Sharing your account with others is prohibited.</li>
            <li><strong>Account Suspension:</strong> RoomEase reserves the right to suspend or terminate accounts found violating these Terms.</li>
        </ul>
    </section>

    <section className="doc-section">
        <h2>3. User Data and Privacy</h2>
        <p>RoomEase collects limited personal data, including phone numbers, email addresses, gender, and location, strictly for matchmaking purposes.</p>
        <ul>
            <li><strong>Data Retention:</strong> Data will be retained for 5 years unless requested for deletion. Data may also be deleted automatically in cases of prolonged inactivity or account closure.</li>
        </ul>
        <p>RoomEase complies with applicable data protection laws, including the IT Act, 2000 and, where applicable, the General Data Protection Regulation (GDPR). User data will be processed lawfully and transparently.</p>
        <p>Refer to our <Link to="/privacy-policy" className="doc-link">Privacy Policy</Link> for detailed information on data handling.</p>
    </section>

    <section className="doc-section">
        <h2>4. Matchmaking Services</h2>
        <p>RoomEase uses algorithms to match users based on preferences, compatibility, and shared interests.</p>
        <p>Matches are suggestions only, and RoomEase does not guarantee the success of any roommate arrangements.</p>
    </section>

    <section className="doc-section">
        <h2>5. Prohibited Activities</h2>
        <ul>
            <li>Use the App for illegal or unauthorized purposes.</li>
            <li>Misrepresent your identity or provide false information.</li>
            <li>Upload inappropriate content, including but not limited to:
                <ul className="nested-list">
                    <li>Hate speech or discriminatory remarks.</li>
                    <li>Sexual, obscene, or offensive content.</li>
                    <li>False or misleading information.</li>
                </ul>
            </li>
            <li>Harass, abuse, or harm other users.</li>
            <li>Reverse-engineer, modify, or tamper with the App.</li>
        </ul>
    </section>

    <section className="doc-section">
        <h2>6. Community Guidelines</h2>
        <p>Treat all users with respect and avoid hate speech, harassment, or discrimination.</p>
        <p>Users violating community guidelines may face account suspension or legal action.</p>
        <p>RoomEase reserves the right to investigate and take legal action against violators.</p>
    </section>

    <section className="doc-section">
        <h2>7. Reporting and Blocking</h2>
        <p>Users can report profiles engaging in inappropriate behavior or violating community guidelines.</p>
        <ul>
            <li>To report a user, use the in-app “Report” button and provide details of the incident.</li>
            <li>Reported accounts will be reviewed, and RoomEase may suspend or terminate the account if violations are confirmed.</li>
        </ul>
    </section>

    <section className="doc-section">
        <h2>8. Safety Recommendations</h2>
        <ul>
            <li>Always meet potential roommates in public spaces.</li>
            <li>Inform friends or family members about your meeting details.</li>
            <li>Avoid sharing sensitive personal information prematurely.</li>
        </ul>
    </section>

    <section className="doc-section">
        <h2>9. Emergency Clause</h2>
        <p>RoomEase is not liable for users’ personal safety during in-person meetings or roommate arrangements.</p>
        <p>In case of emergencies, users should contact local authorities or emergency services immediately.</p>
        <p>RoomEase strongly advises users to conduct thorough background checks and communicate responsibly before meeting anyone in person.</p>
    </section>

    <section className="doc-section">
        <h2>10. Third-Party Services</h2>
        <p>RoomEase integrates trusted third-party services such as Firebase, Google APIs, and WhatsApp to enhance functionality, including location-based matchmaking and direct communication.</p>
        <h3>WhatsApp Integration:</h3>
        <p>RoomEase provides users with an option to connect directly via WhatsApp through in-app redirection (e.g., clicking “Need a Room” directs you to a WhatsApp chat).</p>
        <p>By using this feature, you acknowledge and agree that communication initiated via WhatsApp is governed by WhatsApp’s Terms of Service and Privacy Policy, and RoomEase is not responsible for the security, reliability, or content of conversations conducted on WhatsApp.</p>
        <p>Users are solely responsible for the messages they send or receive via WhatsApp, and RoomEase disclaims all liability arising from such communications.</p>
        <p>All third-party services are governed by their own Terms and Privacy Policies. RoomEase disclaims responsibility for any misuse, data handling, or damages arising from the use of such third-party services.</p>
    </section>

    <section className="doc-section">
        <h2>11. Subscription and Payment Terms</h2>
        <p>If RoomEase introduces premium features, payment details and pricing will be disclosed.</p>
        <p>Refunds, if applicable, will be processed within 7 business days in compliance with our refund policy.</p>
    </section>

    <section className="doc-section">
        <h2>12. Beta Testing Disclaimer</h2>
        <p>RoomEase may introduce features under beta testing, allowing users to voluntarily opt in.</p>
        <ul>
            <li>Beta features may have bugs, limitations, or interruptions.</li>
        </ul>
        <p>Users opting into beta testing agree to provide feedback to improve the features but are not obligated to do so.</p>
    </section>

    <section className="doc-section">
        <h2>13. Updates to Features and Services</h2>
        <p>RoomEase reserves the right to modify, update, or discontinue any feature, service, or aspect of the App at any time without prior notice.</p>
        <p>Users will be notified about significant updates through in-app notifications or emails.</p>
    </section>

    <section className="doc-section">
        <h2>14. Termination by RoomEase</h2>
        <p>RoomEase reserves the right to terminate user accounts under the following circumstances:</p>
        <ul>
            <li>Violations of these Terms or Community Guidelines.</li>
            <li>Fraudulent or harmful behavior.</li>
            <li>Prolonged inactivity of over 12 months.</li>
        </ul>
        <p>Users will be notified of termination via email or in-app notification.</p>
    </section>

    <section className="doc-section">
        <h2>15. Data Retention and Deletion</h2>
        <p>User data will be retained for 5 years post-account deactivation unless explicitly requested for deletion.</p>
        <p>Users may contact our Grievance Officer to request deletion.</p>
    </section>

    <section className="doc-section">
        <h2>16. Disclaimer of Warranties</h2>
        <p>RoomEase makes no warranty regarding the availability, reliability, or security of its services.</p>
        <p>The App is provided “as-is” and “as-available” without warranties of any kind, including implied warranties of merchantability, fitness for a particular purpose, or non-infringement.</p>
    </section>

    <section className="doc-section">
        <h2>17. Limitation of Liability</h2>
        <p>RoomEase is not responsible for any indirect, incidental, or consequential damages arising from your use of the App.</p>
        <p>By using RoomEase, you agree to waive any claims for such damages.</p>
    </section>

    <section className="doc-section">
        <h2>18. Dispute Resolution</h2>
        <p>RoomEase is not a party to disputes between users.</p>
        <p>Users are encouraged to resolve conflicts amicably or through mediation.</p>
        <p>RoomEase is not responsible for any loss or damage arising from such disputes.</p>
    </section>

    <section className="doc-section">
        <h2>19. Grievance Redressal Mechanism</h2>
        <p>If you have complaints or concerns, contact our Grievance Officer:</p>
        <div className="contact-info">
            <p><strong>Email:</strong> <a href="mailto:support@roomease.co.in">support@roomease.co.in</a></p>
            <p><strong>Phone:</strong> <a href="tel:+919569994536">9569994536</a></p>
        </div>
    </section>

    <section className="doc-section">
        <h2>20. Severability Clause</h2>
        <p>If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.</p>
    </section>

    <section className="doc-section">
        <h2>21. Force Majeure Clause</h2>
        <p>RoomEase is not liable for delays or failures caused by events beyond its reasonable control, including but not limited to natural disasters, government actions, or technical failures.</p>
    </section>

    <section className="doc-section">
        <h2>22. Governing Law and Jurisdiction</h2>
        <p>These Terms shall be governed by and construed in accordance with the laws of India.</p>
        <p>Any disputes will be subject to the exclusive jurisdiction of courts in Delhi.</p>
    </section>

    <section className="doc-section">
        <h2>23. Terms & Conditions Updates - Instagram Data Specific</h2>
        <h3>A. Accuracy of Information Clause</h3>
        <p>By signing up on RoomEase, you agree to provide accurate and truthful information, including your Instagram username if requested. RoomEase does not verify whether the provided Instagram account is real, active, or belongs to you.</p>
        <h3>B. Data Collection Clause</h3>
        <p>We collect Instagram usernames to gather additional user data and improve profile authenticity. However, RoomEase does not guarantee the validity of any Instagram account provided and does not conduct automated verification.</p>
        <h3>C. User Responsibility Clause</h3>
        <p>Users acknowledge that providing an Instagram username does not confirm verification or authenticity. RoomEase is not responsible for any false or misleading Instagram details provided by users.</p>
    </section>

    <footer className="doc-footer">
        <h2>Acknowledgment</h2>
        <p>By using RoomEase, you confirm that you have read, understood, and agree to these Terms and Conditions.</p>
    </footer>
</div>
    </div>
  )
}

export default TermsAndConditionsPage;